import { useMemo, useRef } from 'react';
import { ReactFlow, BaseEdge, EdgeLabelRenderer, EdgeProps, Handle, Node, NodeProps, Position } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import useSize from 'ahooks/lib/useSize';

import {
  calculateNodesAndEdges,
  GroupedWorkspace,
  groupWorkSpacesByDatabase,
  SpaceWidth,
  DatabaseWidth,
} from '@/pages/workgroup/tab/Graph';
import { DatabaseT, TGVersion, WorkGroupT, WorkspaceT } from '@/pages/workgroup/type';
import { SortKey } from './types';
import { Database, Space, SpaceTitle } from '@/pages/workgroup/tab/General';

type DatabaseNodeType = Node<
  {
    database: DatabaseT;
    disableDelete: boolean;
    is_ro?: boolean;
    workspace?: WorkspaceT;
    disableUpdate?: boolean;
  },
  'database'
>;

function DatabaseNode({ data }: NodeProps<DatabaseNodeType>) {
  return (
    <>
      <Handle type="source" position={Position.Right} isConnectable={false} id="right" />
      <Handle type="source" position={Position.Bottom} isConnectable={false} id="bottom" />
      <Handle type="target" position={Position.Left} isConnectable={false} id="left" />
      <Database
        database={data.database}
        disableDelete={data.disableDelete}
        is_ro={data.is_ro}
        workspace={data.workspace}
        disableUpdate={data.disableUpdate}
      />
    </>
  );
}

type WorkspaceNodeType = Node<
  {
    database?: DatabaseT;
    workspace: WorkspaceT;
    workgroup: WorkGroupT;
    versions: TGVersion[];
  },
  'workspace'
>;

function WorkspaceNode({ data }: NodeProps<WorkspaceNodeType>) {
  return (
    <>
      <Handle type="target" position={Position.Left} isConnectable={false} id="left" />
      <Space
        database={data.database}
        workspace={data.workspace}
        showAddon={true}
        workgroup={data.workgroup}
        editable={data.workspace.status !== 'Terminating'}
        versions={data.versions}
      />
    </>
  );
}

// M start point
// L end point
// A arc (rx ry x-axis-rotation large-arc-flag sweep-flag x y)
export function DatabaseEdge(props: EdgeProps & { hideLabel?: boolean }) {
  const { sourceX, sourceY, targetX, targetY, label, hideLabel, ...rest } = props;
  const radiusX = 20;
  const radiusY = 30;
  const edgePath = `M ${sourceX} ${sourceY} L ${sourceX} ${targetY - radiusY} A ${radiusX} ${radiusY} 0 0 0  ${
    sourceX + radiusX
  } ${targetY} L ${targetX} ${targetY}`;

  const labelX = (sourceX + targetX) / 2;
  const labelY = targetY;

  return (
    <>
      <BaseEdge path={edgePath} {...rest} />
      {hideLabel || true ? null : (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              background: '#f8f8f8',
              color: '#B3B3B3',
              fontWeight: '600',
              fontSize: 15,
              padding: '6px 8px',
            }}
            className="nodrag nopan"
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const nodeTypes = { database: DatabaseNode, workspace: WorkspaceNode };
const edgeTypes = { databaseEdge: DatabaseEdge };

export type GraphViewProps = {
  group: WorkGroupT;
  filteredWorkspaces: WorkspaceT[];
  versions: TGVersion[];
  sort: SortKey;
};

function getGroupSortName(g: GroupedWorkspace): string {
  return g.database?.name || g.workspaces[0]?.name || '';
}

function getGroupSortSize(g: GroupedWorkspace): string {
  const rw = g.workspaces.find((w) => w.is_rw);
  return (rw ?? g.workspaces[0])?.workspace_type?.typeName || '';
}

function sortGroups(groups: GroupedWorkspace[], sort: SortKey): GroupedWorkspace[] {
  const sorted = [...groups];
  switch (sort) {
    case 'name_asc':
      return sorted.sort((a, b) => getGroupSortName(a).localeCompare(getGroupSortName(b)));
    case 'name_desc':
      return sorted.sort((a, b) => getGroupSortName(b).localeCompare(getGroupSortName(a)));
    case 'size_asc':
      return sorted.sort((a, b) => getGroupSortSize(a).localeCompare(getGroupSortSize(b)));
    case 'size_desc':
      return sorted.sort((a, b) => getGroupSortSize(b).localeCompare(getGroupSortSize(a)));
    default:
      return sorted;
  }
}

export default function GraphView({ group, filteredWorkspaces, versions, sort }: GraphViewProps) {
  const [css, theme] = useStyletron();
  const ref = useRef<HTMLDivElement | null>(null);
  const size = useSize(ref);

  let maxWidth = size?.width ? size.width - SpaceWidth - DatabaseWidth : 64;
  if (maxWidth >= 280) {
    maxWidth = 280;
  } else if (maxWidth < 256) {
    maxWidth = 256;
  }

  const filteredGroup = useMemo<WorkGroupT>(() => {
    const matchedDatabaseIds = new Set(filteredWorkspaces.map((w) => w.database_id).filter(Boolean));
    const allWorkspaceDatabaseIds = new Set(group.workspaces.map((w) => w.database_id).filter(Boolean));
    return {
      ...group,
      workspaces: filteredWorkspaces,
      tg_databases: group.tg_databases.filter(
        (db) => matchedDatabaseIds.has(db.database_id) || !allWorkspaceDatabaseIds.has(db.database_id)
      ),
    };
  }, [group, filteredWorkspaces]);

  const groupedWorkspaces = useMemo(
    () => sortGroups(groupWorkSpacesByDatabase(filteredGroup), sort),
    [filteredGroup, sort]
  );
  const { nodes, edges, totalHeight, totalWidth } = useMemo(() => {
    return calculateNodesAndEdges(filteredGroup, groupedWorkspaces, theme, maxWidth, versions);
  }, [filteredGroup, groupedWorkspaces, maxWidth, theme, versions]);

  return (
    <div ref={ref} className={css({ width: '100%' })}>
      <div
        className={css({
          display: 'grid',
          gridTemplateColumns: `${DatabaseWidth}px ${maxWidth}px ${SpaceWidth}px`,
          marginBottom: '8px',
        })}
      >
        <SpaceTitle>Database</SpaceTitle>
        <div />
        <SpaceTitle>Workspace</SpaceTitle>
      </div>
      <div
        className={css({
          height: `${totalHeight}px`,
          width: `${totalWidth + 16}px`,
        })}
      >
        <ReactFlow
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          proOptions={{
            hideAttribution: true,
          }}
          nodes={nodes}
          edges={edges}
          zoomOnScroll={false}
          zoomOnPinch={false}
          panOnDrag={false}
          panOnScroll={false}
          zoomOnDoubleClick={false}
          selectionOnDrag={false}
          nodesDraggable={false}
          nodesConnectable={false}
          edgesFocusable={false}
          preventScrolling={false}
        />
      </div>
    </div>
  );
}
